import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CurrencyInput from './CurrencyInput';
import { openCashRegister, getMyAssignedStations } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Reusable modal for opening a cash register session.
 * If the business has multiple active stations, a station selector is shown.
 * If there is only one station, it is auto-selected (no UI change for single-caja businesses).
 *
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onHide - Callback when modal is closed
 * @param {function} onSuccess - Optional callback when cash register is opened successfully
 * @param {function} onCancel - Optional callback when user cancels (closes without opening)
 */
const OpenCashRegisterModal = ({ show, onHide, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [openingAmount, setOpeningAmount] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [stations, setStations] = useState([]);
    const [selectedStationId, setSelectedStationId] = useState('');
    const [loadingStations, setLoadingStations] = useState(false);

    // Fetch the stations the current user is responsible for whenever the modal opens.
    // OWNER/ADMIN will see all active stations (backend decides); EMPLOYEE only sees their assigned ones.
    useEffect(() => {
        if (!show) return;
        setLoadingStations(true);
        getMyAssignedStations()
            .then(res => {
                // Filter to only closed stations (open ones are already occupied by the user themselves)
                const available = res.data.filter(s => !s.is_open);
                setStations(available);
                if (available.length === 1) {
                    setSelectedStationId(available[0].station_id);
                } else {
                    setSelectedStationId('');
                }
            })
            .catch(() => {
                setStations([]);
                setSelectedStationId('');
            })
            .finally(() => setLoadingStations(false));
    }, [show]);

    const handleClose = () => {
        if (!submitting) {
            setOpeningAmount(0);
            setSelectedStationId('');
            if (onCancel) {
                onCancel();
            } else {
                onHide();
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (stations.length > 1 && !selectedStationId) return;

        setSubmitting(true);
        try {
            const payload = { opening_amount: openingAmount };
            if (selectedStationId) payload.station_id = selectedStationId;

            await openCashRegister(payload);
            trackAction('cash_register_opened', { has_station: !!selectedStationId });
            toast.success(t('cashRegisterPage.toasts.opened'));
            setOpeningAmount(0);
            setSelectedStationId('');

            if (onSuccess) {
                await onSuccess();
            } else {
                onHide();
            }
        } catch (error) {
            console.error('Error opening cash register:', error);
            const code = error.response?.data?.code;
            trackAction('cash_register_open_failed', { code: code || null }); // code: identificador técnico, no PII
            if (code === 'CASH_REGISTER_ALREADY_OPEN') {
                toast.error(t('cashRegisterPage.toasts.alreadyOpen'));
            } else if (code === 'STATION_REQUIRED') {
                toast.error(t('cashRegisterPage.toasts.stationRequired', 'Selecciona una estación de caja'));
            } else if (code === 'NOT_STATION_RESPONSIBLE') {
                toast.error(t('cashRegisterPage.toasts.notStationResponsible', 'No estás asignado como responsable de esta estación.'));
            } else {
                toast.error(t('cashRegisterPage.toasts.openFailed'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const showStationSelector = stations.length > 1;
    const submitDisabled = submitting || loadingStations || (showStationSelector && !selectedStationId);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            backdrop={isMobile ? 'static' : true}
        >
            <Modal.Header closeButton>
                <Modal.Title>{t('cashRegisterPage.openModal.title')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {/* Station selector — only shown when multiple active stations exist */}
                    {showStationSelector && (
                        <Form.Group className="mb-3">
                            <Form.Label>{t('cashRegisterPage.stations.selectStation', 'Seleccionar Estación')}</Form.Label>
                            {loadingStations ? (
                                <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                            ) : (
                                <Form.Select
                                    value={selectedStationId}
                                    onChange={e => setSelectedStationId(e.target.value)}
                                    required
                                >
                                    <option value="">{t('cashRegisterPage.stations.selectStationPlaceholder', '-- Elige una estación --')}</option>
                                    {stations.map(s => (
                                        <option key={s.station_id} value={s.station_id}>
                                            {s.station_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>{t('cashRegisterPage.openModal.amountLabel')}</Form.Label>
                        <CurrencyInput
                            value={openingAmount}
                            onChange={(value) => setOpeningAmount(value || 0)}
                            placeholder="0"
                            required
                        />
                        <Form.Text className="text-muted">
                            {t('cashRegisterPage.openModal.amountHint')}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button variant="success" type="submit" disabled={submitDisabled}>
                        {submitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            t('cashRegisterPage.openModal.confirmButton')
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default OpenCashRegisterModal;
